import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/** A page loaded on first use, which {@link preloadRoutes} can also load ahead of time. */
export type PreloadablePage = LazyExoticComponent<ComponentType> & { preload: () => Promise<unknown> };

/**
 * `React.lazy` for a route's page, with a `preload` that fetches the same code early. Both share one
 * load, so a page preloaded in the background renders without waiting. A failed load is not kept, so
 * a later visit tries again.
 */
export function lazyPage(load: () => Promise<{ default: ComponentType }>): PreloadablePage {
  let loading: Promise<{ default: ComponentType }> | undefined;
  const once = () =>
    (loading ??= load().catch((error: unknown) => {
      loading = undefined;
      throw error;
    }));
  return Object.assign(lazy(once), { preload: once });
}

export const isPreloadable = (component: unknown): component is PreloadablePage =>
  typeof component === 'object' && component !== null && typeof (component as PreloadablePage).preload === 'function';
